/**
 * Shared logic for OnboardingPage functionality
 *
 * Contains ONLY code that applies to both mobile and desktop platforms:
 * - URL parameter validation
 * - Onboarding navigation state
 * - Profile completeness checking
 * - Form component and label resolution
 * - Step metadata lookup
 */

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useOnboardingNavigation,
  ONBOARDING_CATEGORIES,
} from "../hooks/useOnboardingNavigation";
import type { OnboardingCategory } from "../hooks/useOnboardingNavigation";
import { isSettingsCategory } from "../types/settings";
import { FORM_COMPONENTS, CATEGORY_LABELS } from "../constants/formRegistry";
import { STEP_METADATA } from "../constants/stepMetadata";
import type { StepMetadata } from "../constants/stepMetadata";
import { useAuth } from "../hooks/useAuth";
import { subscribeToSettings } from "../services/settingsService";
import type { SettingsCategory, ProfileSettingsData } from "../types/settings";
import type { ComponentType } from "react";
import { completeOnboarding } from "../services/onboardingService";

export interface OnboardingPageLogicResult {
  /** Whether the category URL param is valid */
  isValidCategory: boolean;
  /** The validated category (defaults to 'profile' if invalid) */
  category: SettingsCategory;
  /** The raw category param from the URL */
  rawCategory: string | undefined;
  /** The form component to render */
  FormComponent: ComponentType;
  /** Human-readable label for the category */
  categoryLabel: string;
  /** Whether the user's profile is complete (for Done button validation) */
  profileComplete: boolean;
  /** List of missing required profile fields */
  missingFields: string[];
  /** Whether the Next button should be disabled (statistics loading) */
  nextDisabled: boolean;
  /** Step metadata for the current onboarding step */
  stepMetadata: StepMetadata | undefined;
  /** Handler for "Use smart defaults" skip functionality */
  handleSkip: () => void;
  /** Navigation state and handlers from useOnboardingNavigation */
  nav: ReturnType<typeof useOnboardingNavigation>;
}

export function useOnboardingPageLogic(): OnboardingPageLogicResult {
  const { category } = useParams<{ category: string }>();
  const { user } = useAuth();
  const [profileComplete, setProfileComplete] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // Use a default value to satisfy hook requirements (hooks can't be conditional)
  // We'll validate and redirect after calling the hook
  const isOnboardingCategory = (value: string): value is OnboardingCategory => {
    return (ONBOARDING_CATEGORIES as readonly string[]).includes(value);
  };

  const validCategory: OnboardingCategory =
    category && isOnboardingCategory(category) ? category : "profile";
  const nav = useOnboardingNavigation({
    currentCategory: validCategory,
    validate:
      validCategory === "profile" ? async () => profileComplete : undefined,
  });

  // Disable Next button while transitioning or if profile is incomplete
  // On profile screen: require yearOfBirth and isRetired
  // On other screens: allow navigation (form validation happens in handleNext)
  const shouldDisableProfileValidation =
    validCategory === "profile" && !profileComplete;
  const nextDisabled = nav.isTransitioning || shouldDisableProfileValidation;

  // Check if profile fields are complete - this should update whenever profile data changes
  useEffect(() => {
    if (!user) {
      return;
    }

    // Subscribe to profile changes so profileComplete updates in real-time as user fills form
    const unsubscribe = subscribeToSettings<ProfileSettingsData>(
      user.userId,
      "profile",
      (profile) => {
        const missing: string[] = [];

        if (!profile || !profile.yearOfBirth) {
          missing.push("Year of Birth");
        }

        if (!profile || profile.isRetired === null) {
          missing.push("Retirement Status");
        }

        setMissingFields(missing);
        setProfileComplete(missing.length === 0);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const isValidCategory = !!category && isSettingsCategory(category);

  // Lookup form component and label from registry
  const FormComponent = FORM_COMPONENTS[validCategory];
  const categoryLabel = CATEGORY_LABELS[validCategory];

  // Lookup step metadata for the current onboarding step
  const stepMetadata = STEP_METADATA[validCategory as OnboardingCategory];

  const navigate = useNavigate();
  const { updateOnboardingCompleted } = useAuth();
  const handleSkip = async () => {
    if (user) {
      const result = await completeOnboarding(user.userId);
      if (result.ok) {
        // Update AuthContext immediately so ProtectedRoute sees
        // onboardingCompleted=true before the navigation render
        updateOnboardingCompleted(true);
      }
    }
    navigate("/dashboard");
  };

  return {
    isValidCategory,
    category: validCategory,
    rawCategory: category,
    FormComponent,
    categoryLabel,
    profileComplete,
    missingFields,
    nextDisabled,
    stepMetadata,
    handleSkip,
    nav,
  };
}
